/**
 * Audit-log domain schemas (PR #5f).
 *
 * These types are used by both the API (validates query params and shapes
 * the response envelope) and the web client (validates the response body
 * received from GET /admin/audit).
 */

import { z } from 'zod';

export const AuditEventCategorySchema = z.enum([
  'Authentication',
  'Authorization',
  'Quote',
  'Bind',
  'Facility',
  'Entity',
  'User',
  'Role',
  'Engine',
  'Admin',
  'Impersonation',
  'System',
]);

export type AuditEventCategory = z.infer<typeof AuditEventCategorySchema>;

/** Tuple of all audit category values — used in UI filter dropdowns. */
export const AUDIT_CATEGORIES = AuditEventCategorySchema.options;

export const AuditEventSchema = z.object({
  id: z.string().uuid(),
  occurredAt: z.string(),
  category: AuditEventCategorySchema,
  action: z.string(),
  actorUserId: z.string().uuid().nullable(),
  actorDisplayName: z.string().nullable(),
  onBehalfOfUserId: z.string().uuid().nullable(),
  entityId: z.string().uuid().nullable(),
  entityName: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  reasonCode: z.string().nullable(),
  requestId: z.string().nullable(),
});

export type AuditEvent = z.infer<typeof AuditEventSchema>;

/** Query-string parameters accepted by GET /admin/audit. */
export const AuditListQuerySchema = z.object({
  category: AuditEventCategorySchema.optional(),
  /** Partial match on the `action` field (server applies ilike). */
  action: z.string().min(1).max(100).optional(),
  actorUserId: z.string().uuid().optional(),
  entityId: z.string().uuid().optional(),
  /** ISO 8601 date-time string; inclusive lower bound on `occurredAt`. */
  fromDate: z.string().datetime({ offset: true }).optional(),
  /** ISO 8601 date-time string; inclusive upper bound on `occurredAt`. */
  toDate: z.string().datetime({ offset: true }).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(200).default(50),
});

export type AuditListQuery = z.infer<typeof AuditListQuerySchema>;

export const AuditListResponseSchema = z.object({
  data: z.array(AuditEventSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

export type AuditListResponse = z.infer<typeof AuditListResponseSchema>;
